import { useState } from "react";
import { useQuery, useReactiveVar } from "@apollo/client";

import { authenticationStateVar } from "@/apollo/auth-state";
import { AdminPageShell } from "@/components/admin-page-shell";
import { DraftPreviewPanel } from "@/components/draft-preview-panel";
import { EditorWithPreviewLayout } from "@/components/editor-with-preview-layout";
import { FullPageLoading } from "@/components/full-page-loading";
import { PageIntro } from "@/components/page-intro";
import {
  GET_DRAFT_PREVIEW,
  type GetDraftPreviewData,
  type GetDraftPreviewVariables,
} from "@/graphql";
import { useDocumentTitle } from "@/hooks/use-document-title";
import type { UseAutoSaveState } from "@/hooks/use-auto-save";
import { DesignForm } from "@/pages/design/design-form";

export function DesignPage() {
  useDocumentTitle("Design");
  const { selectedAccount } = useReactiveVar(authenticationStateVar);
  const [autoSaveState, setAutoSaveState] = useState<UseAutoSaveState>({
    isPending: false,
    isSaving: false,
    hasUnsavedChanges: false,
    errorMessage: null,
  });

  const { data, loading, error } = useQuery<
    GetDraftPreviewData,
    GetDraftPreviewVariables
  >(GET_DRAFT_PREVIEW, {
    variables: { instagramUsername: selectedAccount },
    skip: !selectedAccount,
  });

  if (!selectedAccount) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center">
        <p className="font-inter text-sm text-neutral-500">
          Select an Instagram account to start designing.
        </p>
      </div>
    );
  }

  if (loading) return <FullPageLoading label="Loading your draft…" />;

  if (error || !data?.draftPreview) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center">
        <p className="font-inter text-sm text-red-600">
          Couldn&apos;t load your draft. Please refresh and try again.
        </p>
      </div>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-8">
        <PageIntro
          title="Design"
          description="Adjust the look and feel of your Sked Link so it stays on-brand while keeping the page clear and readable."
        />

        <EditorWithPreviewLayout
          editor={
            <DesignForm
              instagramUsername={selectedAccount}
              draftPreview={data.draftPreview}
              onAutoSaveStateChange={setAutoSaveState}
            />
          }
          preview={
            <DraftPreviewPanel
              draftPreview={data.draftPreview}
              isSaving={autoSaveState.isSaving}
            />
          }
        />
      </div>
    </AdminPageShell>
  );
}
